import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Upload } from 'lucide-react';
import { cn } from 'lib/utils';

interface Props {
  onBrowse: () => void;
  onFile: (file: File | undefined) => void;
}

const FORMATS = ['XLSX', 'CSV', 'JSON', 'PDF', 'DOCX', 'TXT'];

const SHEET = 'absolute inset-0 rounded-xl border-[1.5px] border-border bg-card transition-transform duration-500 ease-out motion-reduce:transition-none';

/**
 * The file target of the import dialog. Hovering or dragging a file over it
 * fans the stacked sheets out. State, not `group-hover`: that variant is dropped
 * by this app's PostCSS pipeline.
 */
export const ImportDropzone: React.FC<Props> = ({ onBrowse, onFile }) => {
  const { t } = useTranslation();
  const [dragging, setDragging] = useState(false);
  const [hovered, setHovered] = useState(false);
  const active = dragging || hovered;

  return (
    <button
      type="button"
      onClick={onBrowse}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onDragOver={(event) => {
        event.preventDefault();
        setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={(event) => {
        event.preventDefault();
        setDragging(false);
        onFile(event.dataTransfer.files?.[0]);
      }}
      className={cn(
        'flex w-full flex-col items-center justify-center gap-1.5 rounded-2xl border-2 border-dashed px-6 py-9 text-center transition-colors duration-200',
        'focus-visible:!outline-none focus-visible:ring-2 focus-visible:ring-foreground focus-visible:ring-offset-2',
        active ? 'border-primary-600 bg-primary/10' : 'border-border bg-muted/30',
      )}
    >
      <span
        aria-hidden="true"
        className="relative mb-3 block h-[76px] w-[62px] animate-import-float motion-reduce:animate-none"
      >
        <span className={cn(SHEET, active ? '-translate-x-6 -rotate-[15deg]' : '-translate-x-3 -rotate-[8deg]')} />
        <span className={cn(SHEET, active ? 'translate-x-6 rotate-[15deg]' : 'translate-x-3 rotate-[8deg]')} />
        <span
          className={cn(
            SHEET,
            'flex items-center justify-center',
            active && '-translate-y-1.5 border-foreground',
          )}
        >
          <span className="flex h-[30px] w-[30px] items-center justify-center rounded-full bg-primary text-primary-foreground">
            <Upload className="h-4 w-4" strokeWidth={2.4} />
          </span>
        </span>
      </span>

      <span className="text-base font-semibold text-foreground">{t('drop_file_or_browse')}</span>

      <span className="mt-2 flex flex-wrap items-center justify-center gap-1.5">
        {FORMATS.map((format) => (
          <span
            key={format}
            className="rounded-md border border-border bg-card px-1.5 py-0.5 text-[11px] font-semibold tracking-wide text-muted-foreground"
          >
            {format}
          </span>
        ))}
        <span className="ml-1.5 text-xs text-muted-foreground">{t('import_max_size')}</span>
      </span>
    </button>
  );
};

export default ImportDropzone;
